import { useCallback, useEffect, useMemo, useRef } from 'react'
import { sampleAt, traceColor, type LapTrace } from '../analysis/laps'
import { sectionAt, type Section } from '../analysis/sections'
import { accelLevel, accelLevelColor, gpsAccel, accelScale } from '../analysis/accel'
import { onMultiTouch, PINCH_WAIT_MS, pinnedAt, recentlyTouched, setPin } from './ChannelChart'

export interface TrackMapProps {
  traces: LapTrace[]
  /** Cursor position as distance from lap start (m), or null when idle */
  cursorDistance: number | null
  /** Fired when the pointer moves over the trace: distance from lap start of the nearest point, or null */
  onHover?: (distance: number | null) => void
  height?: number
  /** Pin group shared with the charts */
  syncKey?: string
  /** Track sections, marked on the reference lap's line; the one under the anchor is highlighted */
  sections?: Section[]
  /** The lap the sections are measured along */
  refTrace?: LapTrace
  /** Colour the lines by acceleration (green) and braking (red) */
  accel?: boolean
  /** Laps whose line is hidden (by key) */
  hidden?: Set<string>
}

/**
 * GPS trace of each selected lap, drawn as plain lines with no basemap.
 * Positions are projected to local metres around the centroid so the shape is true to scale.
 */
export default function TrackMap({
  traces,
  cursorDistance,
  onHover,
  height = 520,
  syncKey = 'studio',
  sections,
  refTrace,
  accel = false,
  hidden,
}: TrackMapProps) {
  const svgRef = useRef<SVGSVGElement>(null)
  const dragging = useRef(false)
  // One finger on a line waits PINCH_WAIT_MS for a second finger (a zoom) before it anchors.
  const pending = useRef<number | null>(null)
  const timer = useRef(0)
  const place = useCallback(
    (d: number) => {
      setPin(syncKey, d)
      onHover?.(d)
    },
    [syncKey, onHover],
  )
  const settle = useCallback(() => {
    if (pending.current == null) return
    place(pending.current)
    pending.current = null
    dragging.current = true
  }, [place])
  const cancel = useCallback(() => {
    pending.current = null
    dragging.current = false
    clearTimeout(timer.current)
  }, [])
  useEffect(() => onMultiTouch(cancel), [cancel])
  const geo = useMemo(() => project(traces), [traces])
  // Runs of one acceleration level per lap, each a path drawn inside the lap's own stroke.
  // With acceleration on, each lap is two glued lines, each half the plain width: the lap's
  // colour on the left of the direction of travel, the acceleration colour on the right.
  const lanes = useMemo(() => {
    if (!accel || !geo) return null
    const lane = (Math.max(geo.width, geo.height) / 400) * LANE_HALF
    const accels = traces.map((t) => gpsAccel(t.gps))
    const scale = accelScale(accels)
    const lapPaths = geo.points.map((pts, ti) => pathOf(shifted(pts, geo.normals[ti], lane)))
    const runs = traces.map((t, ti) => {
      const pts = shifted(geo.points[ti], geo.normals[ti], -lane)
      const a = accels[ti]
      const out: { color: string; d: string }[] = []
      let level = NaN
      let d = ''
      for (let i = 0; i < t.gps.length - 1; i++) {
        const l = accelLevel(a[i], scale)
        if (l !== level) {
          if (d) out.push({ color: accelLevelColor(level), d })
          level = l
          d = `M${pts[2 * i].toFixed(1)} ${pts[2 * i + 1].toFixed(1)}`
        }
        d += `L${pts[2 * i + 2].toFixed(1)} ${pts[2 * i + 3].toFixed(1)}`
      }
      if (d) out.push({ color: accelLevelColor(level), d })
      return out
    })
    return { lapPaths, runs }
  }, [accel, geo, traces])
  const marks = useMemo(
    () => (geo && sections && refTrace ? sectionMarks(geo, sections, refTrace) : null),
    [geo, sections, refTrace],
  )
  const activeSection = sections ? sectionAt(sections, cursorDistance) : -1

  const cursors = useMemo(() => {
    if (cursorDistance == null || !geo) return []
    return traces.flatMap((t, i) => {
      if (t.gps.length === 0 || hidden?.has(t.key)) return []
      const tLap = sampleAt(t.distance, t.time, cursorDistance) + t.lap.start
      const gt = t.gps.map((g) => g.time)
      const lat = sampleAt(
        gt,
        t.gps.map((g) => g.lat),
        tLap,
      )
      const lon = sampleAt(
        gt,
        t.gps.map((g) => g.lon),
        tLap,
      )
      const [x, y] = geo.toXY(lat, lon)
      return [{ x, y, color: traceColor(t, i), key: t.key }]
    })
  }, [cursorDistance, traces, geo, hidden])

  // Pointer -> local metres -> nearest GPS point on any trace -> distance from that lap's start.
  const hitTest = useCallback(
    (clientX: number, clientY: number): number | null => {
      const svg = svgRef.current
      if (!svg || !geo) return null
      // From screen to map units through the drawn rectangle (WebKit's getScreenCTM ignores the
      // CSS transform of the full-screen zoom; the bounding rect does not), with the viewBox
      // centred in the box as preserveAspectRatio "meet" draws it.
      const r = svg.getBoundingClientRect()
      const vb = svg.viewBox.baseVal
      if (r.width <= 0 || r.height <= 0 || vb.width <= 0 || vb.height <= 0) return null
      const k = Math.min(r.width / vb.width, r.height / vb.height)
      const p = {
        x: vb.x + (clientX - r.left - (r.width - vb.width * k) / 2) / k,
        y: vb.y + (clientY - r.top - (r.height - vb.height * k) / 2) / k,
      }
      let best = Infinity
      let bestTrace = -1
      let bestIdx = -1
      geo.points.forEach((pts, ti) => {
        for (let i = 0; i < pts.length; i += 2) {
          const dx = pts[i] - p.x
          const dy = pts[i + 1] - p.y
          const d2 = dx * dx + dy * dy
          if (d2 < best) {
            best = d2
            bestTrace = ti
            bestIdx = i / 2
          }
        }
      })
      const tolerance = Math.max(geo.width, geo.height) * 0.04
      if (bestTrace < 0 || Math.sqrt(best) > tolerance) return null
      const t = traces[bestTrace]
      const lapTime = t.gps[bestIdx].time - t.lap.start
      return sampleAt(t.time, t.distance, lapTime)
    },
    [geo, traces],
  )

  if (!geo) {
    return (
      <div className="track-map empty" style={{ height }}>
        <span className="muted">No GPS data in the selected laps.</span>
      </div>
    )
  }

  const { minX, minY, width: w, height: h, paths, start } = geo
  const pad = Math.max(w, h) * 0.05
  const viewBox = `${minX - pad} ${minY - pad} ${w + 2 * pad} ${h + 2 * pad}`
  // Stroke widths are in user units (metres); scale them so they look ~2px regardless of track size.
  const unit = Math.max(w, h) / 400

  return (
    <svg
      ref={svgRef}
      className="track-map"
      viewBox={viewBox}
      preserveAspectRatio="xMidYMid meet"
      style={{ height }}
      role="img"
      onMouseMove={(e) => {
        // Unpinned: the anchor follows the pointer while it is on a racing line, else none.
        if (recentlyTouched() || pinnedAt(syncKey) != null) return
        onHover?.(hitTest(e.clientX, e.clientY))
      }}
      onMouseLeave={() => {
        // Pinned anchors stay; the synthesised mouseleave after a touch is ignored.
        if (recentlyTouched() || pinnedAt(syncKey) != null) return
        onHover?.(null)
      }}
      onClick={(e) => {
        // A click on a line pins (or moves) the anchor; a click off the lines clears it.
        if (recentlyTouched()) return
        const d = hitTest(e.clientX, e.clientY)
        setPin(syncKey, d)
        onHover?.(d)
      }}
      onTouchStart={(e) => {
        // Only one finger on a racing line moves the anchor; elsewhere (a scroll, a pinch) it stays.
        if (e.touches.length !== 1) {
          cancel()
          return
        }
        const d = hitTest(e.touches[0].clientX, e.touches[0].clientY)
        if (d == null) return
        pending.current = d
        clearTimeout(timer.current)
        timer.current = window.setTimeout(settle, PINCH_WAIT_MS)
      }}
      onTouchMove={(e) => {
        if (e.touches.length !== 1) return
        if (pending.current != null) {
          clearTimeout(timer.current)
          settle()
        }
        if (!dragging.current) return
        const d = hitTest(e.touches[0].clientX, e.touches[0].clientY)
        if (d != null) place(d)
      }}
      onTouchEnd={() => {
        clearTimeout(timer.current)
        settle()
        dragging.current = false
      }}
      onTouchCancel={cancel}
    >
      <title>GPS trace of selected laps</title>
      {marks && activeSection >= 0 && marks.paths[activeSection] && (
        <path
          className="section-active"
          d={marks.paths[activeSection]}
          fill="none"
          stroke="#e6e8ee"
          strokeOpacity={0.28}
          strokeWidth={9 * unit}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      )}
      {(lanes ? lanes.lapPaths : paths).map((d, i) =>
        hidden?.has(traces[i].key) ? null : (
          <path
            key={traces[i].key}
            d={d}
            fill="none"
            stroke={traceColor(traces[i], i)}
            strokeWidth={(lanes ? LANE_HALF * 2 : LINE_WIDTH) * unit}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ),
      )}
      {lanes?.runs.map((runs, i) =>
        hidden?.has(traces[i].key) ? null : (
          <g key={`accel-${traces[i].key}`} className="accel-runs">
            {runs.map((r, k) => (
              <path
                key={k}
                d={r.d}
                fill="none"
                stroke={r.color}
                strokeWidth={LANE_HALF * 2 * unit}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            ))}
          </g>
        ),
      )}
      {start && (
        <g>
          <circle cx={start[0]} cy={start[1]} r={5 * unit} fill="none" stroke="#e6e8ee" strokeWidth={1.5 * unit} />
          <text x={start[0] + 7 * unit} y={start[1] + 4 * unit} fill="#8b93a7" fontSize={11 * unit}>
            S/F
          </text>
        </g>
      )}
      {marks?.ticks.map((t, i) => (
        <g key={i} className="section-tick">
          <line
            x1={t.x - t.nx * 6 * unit}
            y1={t.y - t.ny * 6 * unit}
            x2={t.x + t.nx * 6 * unit}
            y2={t.y + t.ny * 6 * unit}
            stroke="#e6e8ee"
            strokeWidth={1.8 * unit}
            strokeLinecap="round"
          />
        </g>
      ))}
      {cursors.map((c) => (
        <circle key={c.key} cx={c.x} cy={c.y} r={5 * unit} fill={c.color} stroke="#ffffff" strokeWidth={1.5 * unit} />
      ))}
      <g className="scale">
        <line
          x1={minX}
          y1={minY + h + pad * 0.6}
          x2={minX + 100}
          y2={minY + h + pad * 0.6}
          stroke="#8b93a7"
          strokeWidth={1.5 * unit}
        />
        <text x={minX} y={minY + h + pad * 0.6 - 3 * unit} fill="#8b93a7" fontSize={11 * unit}>
          100 m
        </text>
      </g>
    </svg>
  )
}

/** Plain line width in map units (~2.2 px); with acceleration on, each lane is half of it. */
const LINE_WIDTH = 2.2
const LANE_HALF = LINE_WIDTH / 4

/** Unit normals (left of travel) at each point, from the directions of the neighbouring segments. */
function normalsOf(pts: Float64Array): Float64Array {
  const n = pts.length / 2
  const out = new Float64Array(pts.length)
  for (let i = 0; i < n; i++) {
    const a = Math.max(0, i - 1)
    const b = Math.min(n - 1, i + 1)
    const dx = pts[2 * b] - pts[2 * a]
    const dy = pts[2 * b + 1] - pts[2 * a + 1]
    const len = Math.hypot(dx, dy) || 1
    out[2 * i] = -dy / len
    out[2 * i + 1] = dx / len
  }
  return out
}

function shifted(pts: Float64Array, normals: Float64Array, off: number): Float64Array {
  const out = new Float64Array(pts.length)
  for (let i = 0; i < pts.length; i += 2) {
    out[i] = pts[i] + normals[i] * off
    out[i + 1] = pts[i + 1] + normals[i + 1] * off
  }
  return out
}

function pathOf(pts: Float64Array): string {
  let d = ''
  for (let i = 0; i < pts.length; i += 2) d += `${i === 0 ? 'M' : 'L'}${pts[i].toFixed(1)} ${pts[i + 1].toFixed(1)}`
  return d
}

interface Projection {
  toXY: (lat: number, lon: number) => [number, number]
  minX: number
  minY: number
  width: number
  height: number
  paths: string[]
  /** Projected [x0,y0,x1,y1,...] per trace, for hit testing */
  points: Float64Array[]
  /** Unit normals [nx0,ny0,...] per point, to the left of the direction of travel */
  normals: Float64Array[]
  start?: [number, number]
}

function project(traces: LapTrace[]): Projection | null {
  const pts = traces.flatMap((t) => t.gps)
  if (pts.length < 2) return null
  let lat0 = 0
  let lon0 = 0
  for (const p of pts) {
    lat0 += p.lat
    lon0 += p.lon
  }
  lat0 /= pts.length
  lon0 /= pts.length
  const mPerDegLat = 111_320
  const mPerDegLon = 111_320 * Math.cos((lat0 * Math.PI) / 180)
  // SVG y grows downward, so north is negative y.
  const toXY = (lat: number, lon: number): [number, number] => [(lon - lon0) * mPerDegLon, -(lat - lat0) * mPerDegLat]

  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  const points: Float64Array[] = []
  const paths = traces.map((t) => {
    let d = ''
    const arr = new Float64Array(t.gps.length * 2)
    t.gps.forEach((g, i) => {
      const [x, y] = toXY(g.lat, g.lon)
      arr[i * 2] = x
      arr[i * 2 + 1] = y
      if (x < minX) minX = x
      if (y < minY) minY = y
      if (x > maxX) maxX = x
      if (y > maxY) maxY = y
      d += `${i === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`
    })
    points.push(arr)
    return d
  })
  const normals = points.map(normalsOf)
  const first = traces.find((t) => t.gps.length > 0)?.gps[0]
  const start = first ? toXY(first.lat, first.lon) : undefined
  return { toXY, minX, minY, width: maxX - minX, height: maxY - minY, paths, points, normals, start }
}

interface SectionMarks {
  /** A short line across the reference lap at each boundary, with its unit normal */
  ticks: { x: number; y: number; nx: number; ny: number }[]
  /** The reference lap's line within each section, for the highlight */
  paths: string[]
}

/** Where along the reference lap's GPS line each section starts and ends. */
function sectionMarks(geo: Projection, sections: Section[], ref: LapTrace): SectionMarks | null {
  const gps = ref.gps
  if (gps.length < 2) return null
  const times = gps.map((g) => g.time)
  const xy = gps.map((g) => geo.toXY(g.lat, g.lon))
  // Session time at a distance along the lap, then the projected point there.
  const at = (d: number): [number, number] => {
    const t = sampleAt(ref.distance, ref.time, d) + ref.lap.start
    const lat = sampleAt(
      times,
      gps.map((g) => g.lat),
      t,
    )
    const lon = sampleAt(
      times,
      gps.map((g) => g.lon),
      t,
    )
    return geo.toXY(lat, lon)
  }
  const ticks: SectionMarks['ticks'] = []
  const paths: string[] = []
  for (const sec of sections) {
    if (sec.index > 0) {
      const [x, y] = at(sec.from)
      // Tangent from points a little before and after the boundary; the tick is its normal.
      const [ax, ay] = at(Math.max(0, sec.from - 5))
      const [bx, by] = at(sec.from + 5)
      const len = Math.hypot(bx - ax, by - ay) || 1
      ticks.push({ x, y, nx: -(by - ay) / len, ny: (bx - ax) / len })
    }

    const t0 = sampleAt(ref.distance, ref.time, sec.from) + ref.lap.start
    const t1 = sampleAt(ref.distance, ref.time, sec.to) + ref.lap.start
    const [sx, sy] = at(sec.from)
    let d = `M${sx.toFixed(1)} ${sy.toFixed(1)}`
    for (let i = 0; i < gps.length; i++) {
      if (gps[i].time <= t0) continue
      if (gps[i].time >= t1) break
      d += `L${xy[i][0].toFixed(1)} ${xy[i][1].toFixed(1)}`
    }
    const [ex, ey] = at(sec.to)
    d += `L${ex.toFixed(1)} ${ey.toFixed(1)}`
    paths.push(d)
  }
  return { ticks, paths }
}
